import {defineStreamComponentRegistration} from '@Components/ai-chat-window/lib/stream-component-registration';
import {isOptionalNonEmptyString, isOptionalString} from '@Components/ai-chat-window/lib/stream-component-prop-guards';
import {isPlainObject} from '@Utils/object.util';
import type {ButtonAction} from '@Components/ai-chat-window/lib/chat-button-actions.types';
import {ButtonActionType, DownloadFileType, DownloadImageQuality} from '@Components/ai-chat-window/lib/chat-button-actions.types';
import type {ButtonGroupProps, ButtonSpec} from './button-group.types';
import {BUTTON_GROUP_COMPONENT_TYPE, ButtonStyle} from './button-group.types';
import {ButtonGroup} from './button-group';

const isDownloadAction = (value: Record<string, unknown>): boolean => {
  const isFileType = value.fileType === DownloadFileType.PNG || value.fileType === DownloadFileType.PDF;
  const isQuality = value.imageQuality === DownloadImageQuality.ORIGINAL || value.imageQuality === DownloadImageQuality.ENHANCED_4K;

  return typeof value.hashedId === 'string' && isFileType && isQuality;
};

const isDownloadDesignAction = (value: Record<string, unknown>): boolean => {
  const isPage = value.pageHashedId === null || typeof value.pageHashedId === 'string';

  return typeof value.posterHashedId === 'string' && value.posterHashedId !== '' && isPage;
};

const isTurnIntoVideoAction = (value: Record<string, unknown>): boolean => {
  return typeof value.hashedId === 'string' && value.hashedId !== '' && isOptionalNonEmptyString(value.attachmentKey);
};

const isFillPromptAction = (value: Record<string, unknown>): boolean => {
  return typeof value.text === 'string' && value.text !== '';
};

const isUpsellPremiumAction = (value: Record<string, unknown>): boolean => {
  return isOptionalNonEmptyString(value.promoCode);
};

const isButtonAction = (value: unknown): value is ButtonAction => {
  if (!isPlainObject(value) || typeof value.chatId !== 'string') {
    return false;
  }

  if (value.type === ButtonActionType.DOWNLOAD) {
    return isDownloadAction(value);
  }

  if (value.type === ButtonActionType.DOWNLOAD_DESIGN) {
    return isDownloadDesignAction(value);
  }

  if (value.type === ButtonActionType.TURN_INTO_VIDEO) {
    return isTurnIntoVideoAction(value);
  }

  if (value.type === ButtonActionType.FILL_PROMPT) {
    return isFillPromptAction(value);
  }

  if (value.type === ButtonActionType.UPSELL_PREMIUM) {
    return isUpsellPremiumAction(value);
  }

  return value.type === ButtonActionType.UPSELL_CREDITS || value.type === ButtonActionType.UPGRADE_PREMIUM;
};

const isButtonStyle = (value: unknown): boolean => {
  return value === undefined || value === ButtonStyle.PRIMARY || value === ButtonStyle.SECONDARY || value === ButtonStyle.PREMIUM;
};

const isButtonSpec = (value: unknown): value is ButtonSpec => {
  return isPlainObject(value) && typeof value.label === 'string' && value.label.trim() !== '' && isButtonAction(value.action) && isOptionalString(value.icon) && isButtonStyle(value.style);
};

export const buttonGroupRegistration = defineStreamComponentRegistration(
  BUTTON_GROUP_COMPONENT_TYPE,
  ButtonGroup,
  (props: Record<string, unknown>): ButtonGroupProps | null => {
    if (!Array.isArray(props.buttons) || !props.buttons.every(isButtonSpec)) {
      return null;
    }

    return {buttons: props.buttons};
  }
);
